'use client';

import { useCallback } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useSearchParamsCompat } from './useSearchParamsCompat';

type UrlParamValue = string | number | undefined | null;

type UrlParams = Record<string, UrlParamValue>;

interface UseUrlParamsOptions {
  /**
   * Whether to scroll to top when updating URL
   * @default false
   */
  scroll?: boolean;
}

/**
 * Hook for working with URL parameters
 * 
 * @example
 * ```tsx
 * const { updateParams, getParam } = useUrlParams();
 * 
 * // Update a single parameter
 * updateParams({ page: 1 });
 * 
 * // Update multiple parameters
 * updateParams({ town: 'Vienna', name: 'John', page: 0 });
 * 
 * // Remove a parameter (pass empty string, null or undefined)
 * // Note: value 0 is preserved as valid (e.g., page: 0 for first page)
 * updateParams({ town: '' });
 * 
 * // Get parameter value
 * const town = getParam('town');
 * 
 * // Get all parameters as object
 * const allParams = getAllParams();
 * // { town: 'Vienna', name: 'John', page: '0' }
 * ```
 */
export const useUrlParams = (options: UseUrlParamsOptions = {}) => {
  const { scroll = false } = options;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParamsCompat();

  /**
   * Updates URL parameters
   * Automatically removes parameters with empty values (empty string, null, undefined)
   * Note: value 0 is considered valid and will be preserved (useful for pagination starting from 0)
   */
  const updateParams = useCallback(
    (updates: UrlParams) => {
      const params = new URLSearchParams(searchParams.toString());

      Object.entries(updates).forEach(([key, value]) => {
        // Remove parameter if value is empty (but keep 0 as valid value)
        if (
          value === '' ||
          value === null ||
          value === undefined
        ) {
          params.delete(key);
        } else {
          // Convert number to string (including 0)
          params.set(key, String(value));
        }
      });

      const newUrl = `${pathname}?${params.toString()}`;
      router.replace(newUrl, { scroll });
    },
    [searchParams, pathname, router, scroll],
  );

  /**
   * Gets parameter value from URL
   * @param key - parameter key
   * @param defaultValue - default value if parameter is missing
   */
  const getParam = useCallback(
    (key: string, defaultValue: string = ''): string => {
      return searchParams.get(key) || defaultValue;
    },
    [searchParams],
  );

  /**
   * Gets numeric parameter value from URL
   * @param key - parameter key
   * @param defaultValue - default value if parameter is missing or not a number
   */
  const getNumberParam = useCallback(
    (key: string, defaultValue: number = 0): number => {
      const value = searchParams.get(key);
      if (!value) return defaultValue;
      const num = Number(value);
      return Number.isNaN(num) ? defaultValue : num;
    },
    [searchParams],
  );

  /**
   * Gets all parameters from URL as an object
   * @returns Object with all URL parameters as key-value pairs
   */
  const getAllParams = useCallback((): Record<string, string> => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }, [searchParams]);

  /**
   * Checks if parameter exists in URL
   */
  const hasParam = useCallback(
    (key: string): boolean => {
      return searchParams.has(key);
    },
    [searchParams],
  );

  /**
   * Removes one or more parameters from URL
   */
  const deleteParams = useCallback(
    (...keys: string[]) => {
      const params = new URLSearchParams(searchParams.toString());
      keys.forEach((key) => params.delete(key));
      const newUrl = `${pathname}?${params.toString()}`;
      router.replace(newUrl, { scroll });
    },
    [searchParams, pathname, router, scroll],
  );

  /**
   * Clears all parameters from URL
   */
  const clearParams = useCallback(() => {
    router.replace(pathname, { scroll });
  }, [pathname, router, scroll]);

  return {
    updateParams,
    getParam,
    getNumberParam,
    getAllParams,
    hasParam,
    deleteParams,
    clearParams,
    searchParams, // Direct access to search params instance
  };
};

